import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { RemoveScroll } from 'react-remove-scroll';

import { IconButton } from '~shared/components/buttons';
import { IconX } from '~shared/components/icons/large';
import { ReactNodeNoStrings } from '~shared/types/ReactNodeNoStrings';
import { normalizeKey } from '~shared/utils/normalize-keycode';
import { render } from '~shared/utils/react';

import { DialogTitle, TitleProps } from './DialogTitle';

/**
 * Size determines the max-width of the dialog above the `sm` breakpoint. Below
 * this breakpoint the max-width is 100%.
 */
const SIZE_CLASS = {
  default: 'sm:max-w-modal',
  medium: 'sm:max-w-modal-md',
  large: 'min-w-max lg:max-w-modal-lg',
  extraLarge: 'lg:h-modal-lg sm:max-w-modal-xl',
  content: '',
  full: 'h-full w-full',
} as const;

const PADDING_CLASS = {
  none: {
    card: 'p-0',
    close: 'pt-6 pr-6',
  } as const,
  default: {
    card: 'p-8 pt-6',
    close: 'pt-6 pr-6',
  } as const,
  confirmation: {
    card: 'p-12 sm:p-16',
    close: 'pt-6 pr-6',
  } as const,
} as const;

const POSITION_CLASS = {
  center: 'items-center justify-center',
  top: 'items-start justify-center',
  right: 'items-center justify-end',
  left: 'items-center justify-start',
} as const;

export interface DialogContentProps {
  children:
    | React.ReactNode
    | (({ headingRef, title, description, descriptionId }) => React.ReactNode);
  id: string;
  size?: keyof typeof SIZE_CLASS;
  padding?: keyof typeof PADDING_CLASS;
  position?: keyof typeof POSITION_CLASS;
  __customClassEscapeHatch?: string;
  description?: ReactNodeNoStrings;
  onClose: () => void;
  /** defaults to `Close` if not supplied */
  closeLabel?: string;
  /** Use this if a custom header includes its own close button. */
  hasCustomCloseButton?: boolean;
  scrollLock?: boolean;
  dialogRef?: React.Ref<HTMLElement>;
  headingRef?: React.Ref<HTMLElement>;
  title: React.ReactNode;
  ['aria-label']?: string;
  titleWeight?: TitleProps['weight'];
}

export interface DialogHeaderProps
  extends Pick<DialogContentProps, 'description'> {
  title: React.ReactNode;
  descriptionId: string;
  weight?: TitleProps['weight'];
}

const DialogHeader = React.forwardRef<HTMLElement, DialogHeaderProps>(
  function DialogHeader(
    { title, description, descriptionId, weight = 'bold' }: DialogHeaderProps,
    ref
  ) {
    return (
      <header className="m-0 relative">
        <h3 className="flex m-0 py-2">
          <DialogTitle weight={weight} ref={ref}>
            {title}
          </DialogTitle>
        </h3>
        {description ? <div id={descriptionId}>{description}</div> : null}
      </header>
    );
  }
);

export function DialogContent({
  children,
  id,
  size = 'default',
  padding = 'default',
  position = 'center',
  __customClassEscapeHatch,
  title,
  description,
  onClose,
  closeLabel = translate('Close'),
  hasCustomCloseButton = false,
  scrollLock = true,
  dialogRef: dialogRefProp,
  headingRef: headingRefProp,
  titleWeight = 'bold',
  ...props
}: DialogContentProps) {
  const defaultDialogRef = React.useRef<HTMLElement>(null);
  const defaultHeadingRef = React.useRef<HTMLElement>(null);
  const dialogRef = dialogRefProp || defaultDialogRef;
  const headingRef = headingRefProp || defaultHeadingRef;
  const descriptionId = `${id}-desc`;
  const isDrawer = position === 'right' || position === 'left';

  const handleEscape = (event: React.KeyboardEvent) => {
    const targetKey = normalizeKey(event);

    if (targetKey === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  const slot = React.useMemo(
    () => ({
      title,
      description,
      descriptionId,
      headingRef,
      weight: titleWeight,
    }),
    [title, description, descriptionId, headingRef, titleWeight]
  );

  const dialogContainerClass = cx([
    'relative flex flex-col max-w-full max-h-full',
    POSITION_CLASS[position],
    size !== 'content' && ['w-full', SIZE_CLASS[size]],
    !['default', 'content'].includes(size) && 'h-full',
    isDrawer && 'h-full',
  ]);
  const dialogCardClass = cx([
    'relative flex flex-col h-full sm:h-auto font-sans bg-white overflow-y-auto shadow-xl pointer-events-auto antialiased',
    PADDING_CLASS[padding].card,
    size !== 'content' && 'w-full',
    !['default', 'content'].includes(size) && 'h-full sm:h-auto',
    size === 'extraLarge' && 'lg:h-full',
    isDrawer ? 'flex-1' : 'sm:rounded-6',
    __customClassEscapeHatch,
  ]);

  if (__DEV__) {
    if (typeof title !== 'string' && !props['aria-label']) {
      console.warn(
        'title prop is not a string, and aria-label is missing. Please specify an aria-label'
      );
    }
  }

  return (
    <div
      className={`relative flex ${POSITION_CLASS[position]} w-full h-full`}
      id={id}
      data-testid={id}
      role="dialog"
      aria-label={typeof title === 'string' ? title : props['aria-label']}
      aria-describedby={description ? descriptionId : undefined}
      aria-modal={true}
      onKeyDown={handleEscape}
    >
      <div className={dialogContainerClass}>
        <RemoveScroll ref={dialogRef} forwardProps enabled={scrollLock}>
          <div className={dialogCardClass}>
            {typeof children === 'function' ? (
              render({ children }, slot, React.Fragment, 'DialogRenderContent')
            ) : (
              <>
                <DialogHeader {...slot} ref={headingRef} />
                {children}
              </>
            )}
            {hasCustomCloseButton ? null : (
              <div className="absolute top-0 left-0 right-0 z-100 flex justify-center pointer-events-none">
                <div
                  className={`flex justify-end w-full ${PADDING_CLASS[padding].close}`}
                >
                  <div className="flex pointer-events-auto">
                    <IconButton
                      appearance="ghost"
                      aria-label={closeLabel}
                      onClick={onClose}
                    >
                      <IconX />
                    </IconButton>
                  </div>
                </div>
              </div>
            )}
          </div>
        </RemoveScroll>
      </div>
    </div>
  );
}
